import InputWLabel from '@components/interface/InputWLabel';
import type { FC } from 'react';
import { useEffect } from 'react';
import OnBoardingSection from '../components/OnBoardingSection';
import SelectDropDownCountries from '@components/interface/SelectDropDownCountries';
import { type MissingCommerceKeys, useGetTimezones } from '@pages/Commerce/hooks/commerce';
import { formattedCountries } from '@utils/countryCodes';
import SelectDropDown from '@components/interface/SelectDropDown';
import { Commerce } from '@interfaces/general';
import { IconInfoSquareRounded, IconReceipt } from '@tabler/icons-react';
import { Tooltip } from '@plick/ui';

type GeneralInfoProps = {
  commerce: Commerce;
  missingValues: MissingCommerceKeys[];
  handleUpdateValue: <K extends keyof Commerce>(key: K, value: Commerce[K]) => void;
};

const GeneralInfo: FC<GeneralInfoProps> = ({ commerce, missingValues, handleUpdateValue }) => {
  const { timeZones } = useGetTimezones();

  useEffect(() => {
    if (!commerce.zona_horaria) {
      handleUpdateValue('zona_horaria', 'America/Mexico_City');
    }
  }, [commerce.zona_horaria, handleUpdateValue]);

  return (
    <OnBoardingSection title="Este es el número donde llegarán tus pedidos:">
      <div className="flex gap-4">
        <div className="w-[40%] lg:w-[35%]">
          <SelectDropDownCountries
            label="Código:"
            placeHolder="ej. +52"
            error={missingValues.includes('country_code')}
            values={formattedCountries.find((country) => country.value === commerce.country_code?.toString()) || null}
            options={formattedCountries}
            onChange={(value) => {
              handleUpdateValue('country_code', parseInt(value.value));
            }}
          />
        </div>
        <div className="w-[60%] lg:w-[65%]">
          <InputWLabel
            textLabel="Número de WhatsApp"
            textPlaceHolder="Número al que te llegarán los pedidos por WhatsApp"
            type="tel"
            value={(commerce.telefono || '').toString()}
            error={missingValues.includes('telefono')}
            errorMessages={
              !commerce.telefono ? 'Requerido' : 'El número debe tener 10 caracteres, sin espacios ni guiones.'
            }
            onChange={(value) =>
              handleUpdateValue('telefono', value ? parseInt(value.replaceAll(' ', '').replaceAll('-', '')) : undefined)
            }
          />
        </div>
      </div>
      <div className="flex gap-2 items-center">
        <InputWLabel
          textLabel="Prefijo para folio de pedidos"
          postValue="-00000"
          error={missingValues.includes('prefijo_folio')}
          textPlaceHolder="Escribe iniciales de tu negocio"
          max={5}
          type="text"
          value={commerce.prefijo_folio || ''}
          onChange={(value) => handleUpdateValue('prefijo_folio', value.toUpperCase())}
        />
        <div className="z-10 mt-3">
          <Tooltip
            align="center"
            header={{
              icon: <IconReceipt stroke={2} />,
              title: 'ABCD-12345',
            }}
            position="left"
            text="Tu folio en el ticket se vera asi ☝️​"
          >
            <IconInfoSquareRounded stroke={2} />
          </Tooltip>
        </div>
      </div>

      <div className="flex gap-4">
        <SelectDropDown
          label="Zona horaria:"
          placeHolder="Selecciona la zona horaria"
          error={missingValues.includes('zona_horaria')}
          values={timeZones.find((timeZone) => timeZone.value === commerce.zona_horaria) || null}
          options={timeZones}
          onChange={(value) => {
            handleUpdateValue('zona_horaria', value.value);
          }}
        />
        <div className="flex flex-col mb-2.5 w-full">
          <span className={'font-medium'}>Hora actual:</span>
          <div className="relative flex text-gray-600 focus-within:text-gray-400 items-center">
            <span className="bg-color-primary w-full py-2 text-sm text-color-white bg-gray-900 rounded-md p-2 focus:outline-none focus:bg-white focus:text-gray-900 transition-colors placeholder:text-color-gray_dark">
              {commerce.zona_horaria
                ? new Date()
                    .toLocaleTimeString(undefined, { timeZone: commerce.zona_horaria })
                    .replace(/(.*)\D\d+/, '$1')
                : ''}
            </span>
          </div>
        </div>
      </div>
    </OnBoardingSection>
  );
};

export default GeneralInfo;
